import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import parse, { DOMNode, Element, domToReact } from 'html-react-parser';
import { 
  Building2, 
  MapPin, 
  Globe2, 
  CheckCircle2, 
  Briefcase, 
  Award, 
  Gift, 
  Send, 
  ShieldCheck, 
  ChevronDown, 
  ChevronUp, 
  FileText,
  Lock,
  ExternalLink
} from 'lucide-react';

interface JobDescriptionRendererProps {
  description: string;
  location?: string;
  headquarters?: string;
  employmentType?: string;
  sourceUrl?: string | null;
  className?: string;
  compact?: boolean;
  isExternal?: boolean;
}

interface ParsedSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string;
}

export const JobDescriptionRenderer: React.FC<JobDescriptionRendererProps> = ({
  description,
  location = 'Remote',
  headquarters,
  employmentType = 'Full-Time / Contract',
  sourceUrl,
  className = '',
  compact = false,
  isExternal = false
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (id: string) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // 1. Sanitize & clean raw content
  const sanitizedHtml = useMemo(() => {
    if (!description) return '<p>No extended project description provided.</p>';

    let raw = description;

    // Remove GDPR compliance boilerplate
    raw = raw.replace(/<p>\s*<em>\s*GDPR\s+Compliance:[\s\S]*?<\/em>\s*<\/p>/gi, '');
    raw = raw.replace(/GDPR\s+Compliance:[\s\S]*$/gi, '');

    // Strip scraper artifacts
    raw = raw.replace(/\[(?:Contact\s+Info\s+Protected|Link\s+Protected|Email\s+Protected|Phone\s+Protected|LinkProtected|EmailProtected|PhoneProtected)\]/gi, '');

    // Clean feed number / debug scrapings if lingering
    raw = raw.replace(/\(Feed\s*#?\d+\)/gi, '');
    raw = raw.replace(/\[Feed\s*#?\d+\]/gi, '');

    // Allow semantic tags
    const clean = DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
        'p', 'b', 'strong', 'i', 'em', 'u', 's', 'strike',
        'ul', 'ol', 'li', 'br', 'hr', 'blockquote', 'code', 'pre',
        'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'a'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'title', 'id']
    });

    return clean;
  }, [description]);

  // 2. Identify semantic sections if available (Responsibilities, Qualifications, Benefits, etc.)
  const { sections, hasStructuredSections, generalContent } = useMemo(() => {
    if (!sanitizedHtml) return { sections: [], hasStructuredSections: false, generalContent: '' };

    const sectionConfigs = [
      {
        id: 'overview',
        title: 'Company Overview & Background',
        icon: <Building2 className="w-4 h-4 text-[#3A41A5]" />,
        regex: /(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Company\s+Overview|About\s+(?:Us|The\s+Company|Our\s+Team)|Who\s+We\s+Are)\s*(?:<\/h[1-4]>|<\/strong>|<\/b>)([\s\S]*?)(?=(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Key\s+Responsibilities|Responsibilities|What\s+You'll\s+Do|Qualifications|Requirements|What\s+We're\s+Looking\s+For|Benefits|Perks|How\s+to\s+Apply)|$)/i
      },
      {
        id: 'responsibilities',
        title: 'Key Responsibilities',
        icon: <Briefcase className="w-4 h-4 text-[#0284c7]" />,
        regex: /(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Key\s+Responsibilities|Responsibilities|What\s+You'll\s+Do|Duties|Core\s+Tasks)\s*(?:<\/h[1-4]>|<\/strong>|<\/b>)([\s\S]*?)(?=(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Qualifications|Requirements|What\s+We're\s+Looking\s+For|What\s+You\s+Need|Benefits|Perks|How\s+to\s+Apply)|$)/i
      },
      {
        id: 'qualifications',
        title: 'Qualifications & Requirements',
        icon: <Award className="w-4 h-4 text-purple-600" />,
        regex: /(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Qualifications|Requirements|What\s+We're\s+Looking\s+For|What\s+You\s+Bring|Skills\s+&\s+Experience)\s*(?:<\/h[1-4]>|<\/strong>|<\/b>)([\s\S]*?)(?=(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Benefits|Perks|Compensation|What\s+We\s+Offer|How\s+to\s+Apply)|$)/i
      },
      {
        id: 'benefits',
        title: 'Benefits & Compensation',
        icon: <Gift className="w-4 h-4 text-emerald-600" />,
        regex: /(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:Benefits|Perks|Compensation|What\s+We\s+Offer|Why\s+Join\s+Us)\s*(?:<\/h[1-4]>|<\/strong>|<\/b>)([\s\S]*?)(?=(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:How\s+to\s+Apply|Application\s+Process)|$)/i
      },
      {
        id: 'how_to_apply',
        title: 'How to Apply',
        icon: <Send className="w-4 h-4 text-amber-600" />,
        regex: /(?:<h[1-4][^>]*>|<strong>|<b[^>]*>)\s*(?:How\s+to\s+Apply|Application\s+Process|Next\s+Steps)\s*(?:<\/h[1-4]>|<\/strong>|<\/b>)([\s\S]*?)$/i
      }
    ];

    const foundSections: ParsedSection[] = [];
    for (const conf of sectionConfigs) {
      const match = sanitizedHtml.match(conf.regex);
      if (match && match[1] && match[1].trim().length > 20) {
        foundSections.push({
          id: conf.id,
          title: conf.title,
          icon: conf.icon,
          content: match[1].trim()
        });
      }
    }

    return {
      sections: foundSections,
      hasStructuredSections: foundSections.length >= 2,
      generalContent: sanitizedHtml
    };
  }, [sanitizedHtml]);

  // 3. HTML parser replacement rules for rich typography
  const parserTransform = (domNode: DOMNode) => {
    if (domNode instanceof Element) {
      // Style headers
      if (domNode.name === 'h1' || domNode.name === 'h2') {
        return (
          <h3 className="text-base font-extrabold text-slate-900 mt-5 mb-2.5 flex items-center gap-2 border-b border-slate-100 pb-1.5">
            <span className="w-1.5 h-4 bg-[#3A41A5] rounded-full inline-block" />
            {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
          </h3>
        );
      }
      if (domNode.name === 'h3' || domNode.name === 'h4') {
        return (
          <h4 className="text-sm font-bold text-slate-800 mt-4 mb-2">
            {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
          </h4>
        );
      }
      // Style unordered lists
      if (domNode.name === 'ul') {
        return (
          <ul className="space-y-1.5 my-3 pl-2 text-slate-700">
            {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
          </ul>
        );
      }
      // Style ordered lists
      if (domNode.name === 'ol') {
        return (
          <ol className="space-y-1.5 my-3 pl-5 list-decimal text-slate-700">
            {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
          </ol>
        );
      }
      // Style list items with clean checkmark or dot
      if (domNode.name === 'li') {
        return (
          <li className="flex items-start gap-2 text-xs sm:text-sm leading-relaxed text-slate-700">
            <span className="text-[#3A41A5] mt-1 shrink-0 font-bold">•</span>
            <div className="flex-1">
              {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
            </div>
          </li>
        );
      }
      // Style paragraphs
      if (domNode.name === 'p') {
        return (
          <p className="text-xs sm:text-sm leading-relaxed text-slate-700 my-2">
            {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
          </p>
        );
      }
      // Style strong/bold
      if (domNode.name === 'strong' || domNode.name === 'b') {
        return (
          <strong className="font-bold text-slate-900">
            {domToReact(domNode.children as DOMNode[], { replace: parserTransform })}
          </strong>
        );
      }
      // Style protected links badges
      if (domNode.attribs && domNode.attribs.class && domNode.attribs.class.includes('inline-badge-protected')) {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 my-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-[#3A41A5] border border-indigo-100 shadow-2xs">
            <Lock className="w-3 h-3 text-[#3A41A5]" />
            <span>Escrow-Protected Link</span>
          </span>
        );
      }
    }
  };

  return (
    <div className={`space-y-5 text-left ${className}`}>
      
      {/* Systematic Job Metadata Header */}
      <div className="bg-gradient-to-r from-slate-50 to-indigo-50/40 border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-2xs">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
          
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-100/80 text-[#3A41A5] font-bold text-xs">
              <Globe2 className="w-3.5 h-3.5" />
              Location: Remote
            </span>
            <span className="text-slate-300">|</span>
            <span className="inline-flex items-center gap-1.5 text-slate-600 font-medium text-xs">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              Headquarters: <strong className="text-slate-800">{headquarters || 'Global / USA'}</strong>
            </span>
          </div>

          {!isExternal && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100/80 text-emerald-800 font-bold text-xs">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Escrow Milestone Protected
              </span>
            </div>
          )}

        </div>
      </div>

      {/* Render Structured Sections or Formatted General Content */}
      {hasStructuredSections && !compact ? (
        <div className="space-y-4">
          {sections.map(section => {
            const isCollapsed = !!collapsedSections[section.id];
            return (
              <div 
                key={section.id} 
                className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-2xs transition-all hover:border-slate-300"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 sm:p-4.5 bg-slate-50/70 hover:bg-slate-100/60 transition cursor-pointer text-left"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-white border border-slate-200/70 shadow-2xs">
                      {section.icon}
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                      {section.title}
                    </h3>
                  </div>
                  <div className="text-slate-400 hover:text-slate-600 transition">
                    {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="p-4 sm:p-5 text-slate-700 text-xs sm:text-sm leading-relaxed border-t border-slate-100">
                    {parse(section.content, { replace: parserTransform })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Formatted Semantic JSX Container */
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 sm:p-6 shadow-2xs text-slate-700 leading-relaxed font-sans space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100 text-slate-800 font-bold text-sm">
            <FileText className="w-4 h-4 text-[#3A41A5]" />
            <span>Position Scope &amp; Deliverables</span>
          </div>
          <div className="prose prose-sm max-w-none text-slate-700">
            {parse(sanitizedHtml, { replace: parserTransform })}
          </div>
        </div>
      )}

      {/* Escrow Contract Protection Reminder Box (Native Jobs Only) */}
      {!isExternal && (
        <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-4 sm:p-4.5 flex items-start gap-3 text-xs text-emerald-900">
          <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold text-emerald-950">HarryDesk Escrow Milestone Protection</p>
            <p className="text-emerald-800 leading-relaxed text-xs">
              All contracts initiated on HarryDesk are pre-funded into neutral escrow vaults before work begins. Freelancers enjoy low 2% milestone fees (0% on Freelancer Pro) with guaranteed payment release upon milestone approval.
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default JobDescriptionRenderer;
